/**
 * Draft state and validation.
 *
 * Kept pure and separate from the components so the rules are testable without
 * a DOM. These checks are a convenience for the founder -- the API re-runs all
 * of them server-side, and that is the copy that actually decides.
 */
import {
  HOSTING_WINDOWS,
  addMonths,
  findWindow,
  moderation,
  SLUG_REJECTION_MESSAGES,
  slugify,
  validateLaunchDate,
  validateSlug,
  zonedMidnightToUtc,
  type WindowId,
} from '@simplelaunch/shared'
import { DEFAULT_THEME, type Site, type ThemeChoice } from '@simplelaunch/renderer'

export const NAME_MAX = 40
export const TAGLINE_MAX = 120

export interface Draft {
  readonly name: string
  readonly tagline: string
  /** Empty until the founder edits it; otherwise derived from the name. */
  readonly slugOverride: string
  readonly slugTouched: boolean
  /** YYYY-MM-DD, read in the founder's own timezone. */
  readonly launchDate: string
  readonly windowId: WindowId
  readonly theme: ThemeChoice
  readonly waitlist: boolean
}

export type DraftField = 'name' | 'tagline' | 'slug' | 'launchDate'

export interface DraftIssue {
  readonly field: DraftField
  readonly message: string
}

export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Calendar date N days from now, as YYYY-MM-DD -- for input defaults and caps. */
export function isoDate(from: Date, addDays = 0): string {
  const d = new Date(from.getTime() + addDays * 86_400_000)
  return d.toISOString().slice(0, 10)
}

export function emptyDraft(now: Date): Draft {
  return {
    name: '',
    tagline: '',
    slugOverride: '',
    slugTouched: false,
    launchDate: isoDate(now, 30),
    windowId: 'window_6mo',
    theme: DEFAULT_THEME,
    waitlist: true,
  }
}

/** The slug actually used: the founder's if they edited it, else derived. */
export function effectiveSlug(draft: Draft): string {
  return draft.slugTouched ? draft.slugOverride : slugify(draft.name)
}

/** When hosting would end if they paid right now. */
export function projectedExpiry(draft: Draft, now: Date): Date {
  const window = findWindow(draft.windowId) ?? HOSTING_WINDOWS[1]
  if (!window) throw new Error('no hosting windows configured')
  return addMonths(now, window.months)
}

export function launchInstant(draft: Draft, timeZone: string): Date | undefined {
  try {
    return zonedMidnightToUtc(draft.launchDate, timeZone)
  } catch {
    return undefined
  }
}

export function validateDraft(draft: Draft, now: Date, timeZone: string): readonly DraftIssue[] {
  const issues: DraftIssue[] = []
  const slug = effectiveSlug(draft)

  if (draft.name.trim().length === 0) {
    issues.push({ field: 'name', message: 'Your startup needs a name.' })
  } else if (draft.name.length > NAME_MAX) {
    issues.push({ field: 'name', message: `Keep it to ${NAME_MAX} characters.` })
  }

  if (draft.tagline.length > TAGLINE_MAX) {
    issues.push({ field: 'tagline', message: `Keep it to ${TAGLINE_MAX} characters.` })
  }

  // Only complain about the slug once there is a name to derive one from,
  // otherwise an empty form shows two errors for one missing field.
  if (draft.name.trim().length > 0) {
    const check = validateSlug(slug)
    if (!check.ok) {
      issues.push({ field: 'slug', message: SLUG_REJECTION_MESSAGES[check.reason] })
    }
  }

  const instant = launchInstant(draft, timeZone)
  if (!instant) {
    issues.push({ field: 'launchDate', message: 'Pick a launch date.' })
  } else {
    const check = validateLaunchDate(instant, now, projectedExpiry(draft, now))
    if (!check.ok) {
      issues.push({
        field: 'launchDate',
        message:
          check.reason === 'in-past'
            ? 'Pick a date in the future.'
            : 'That is past the end of your hosting window. Choose a longer window, or an earlier date.',
      })
    }
  }

  // Moderation last: a founder mid-typing should see "needs a name" before
  // they see anything about brand similarity.
  const findings = moderation.screen({ name: draft.name, tagline: draft.tagline, slug })
  for (const finding of findings) {
    const field: DraftField = finding.field === 'slug' ? 'slug' : finding.field
    if (issues.some((i) => i.field === field)) continue
    issues.push({ field, message: moderation.MODERATION_MESSAGES[finding.code] })
  }

  return issues
}

export function issueFor(issues: readonly DraftIssue[], field: DraftField): string | undefined {
  return issues.find((i) => i.field === field)?.message
}

/** Build the Site the renderer wants. Preview and publish use the same shape. */
export function draftToSite(draft: Draft, timeZone: string, now: Date): Site {
  const instant = launchInstant(draft, timeZone) ?? new Date(now.getTime() + 86_400_000)
  return {
    siteId: 'preview',
    slug: effectiveSlug(draft) || 'your-startup',
    name: draft.name.trim() || 'Your Startup',
    tagline: draft.tagline.trim(),
    launchAt: instant.toISOString(),
    timeZone,
    theme: draft.theme,
    waitlist: draft.waitlist,
  }
}
